/**
 * Every saved segment in one place, with its count: the person's own
 * ("Mine") and the ones shared with them ("Shared").
 *
 * A private segment is a saved filter tab (see SegmentsRail), so this reads
 * and writes the same `/api/v1/me/filter-tabs` rows as the leads page:
 * renaming or deleting here is the same as doing it from the rail. Open goes
 * to `/leads?segment=<id>`, or `/leads?segment=shared:<id>` for a shared one,
 * which makes it the open segment there.
 *
 * A shared segment's count runs as the person looking, like the list it
 * opens: it shares filters, not results.
 *
 * There is no "last changed" column for private segments. They are stored as
 * one row per person, so the only timestamp is for the whole set.
 */
import { useState, type ReactNode } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, Group, Loader, Modal, Stack, Table, Text, TextInput, Tooltip,
} from '@mantine/core';
import { IconArrowRight, IconCopy, IconPencil, IconShare, IconTrash, IconUsers } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  segmentFilters, sharedKey, useDeleteSharedSegment, useFilterTabs, useSaveFilterTabs, useSegmentCounts,
  useSharedSegmentCounts, useSharedSegments, useUpdateSharedSegment, type FilterTab, type SharedSegment,
} from '../api/queries';
import { toFilters } from '../api/filterState';
import { FONTS, TOKENS, formatNumber } from '../theme';
import { MonoLabel, PageHeader, SpeedChip } from '../ui';
import { navLabel, useVocab } from '../vocab';
import { activeFilterCount } from './leadsFilterModel';
import { sharedByLine } from './SegmentsRail';
import { ShareSegmentModal, type ShareTarget } from './ShareSegmentModal';
import { UsedByButton } from './workflows/UsedByPanel';

function filtersText(n: number) {
  return n === 0 ? 'None' : n === 1 ? '1 filter' : `${n} filters`;
}

function CountCell({ n, pending }: { n: number | undefined; pending: boolean }) {
  return (
    <Text component="span" size="sm" style={{ fontFamily: FONTS.mono, fontVariantNumeric: 'tabular-nums' }}>
      {n === undefined ? (pending ? '…' : '') : formatNumber(n)}
    </Text>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack gap={6}>
      <MonoLabel>{title}</MonoLabel>
      <Card p={0}>{children}</Card>
    </Stack>
  );
}

type Renaming = { kind: 'private'; id: number; name: string } | { kind: 'shared'; id: string; name: string }
  | { kind: 'copy'; of: SharedSegment; name: string };

export function SegmentsPage() {
  const v = useVocab();
  const navigate = useNavigate();
  const tabsQuery = useFilterTabs();
  const saveTabs = useSaveFilterTabs();
  const stored = tabsQuery.data;
  const counts = useSegmentCounts(stored?.tabs);
  const sharedQuery = useSharedSegments();
  const shared = sharedQuery.data?.segments ?? [];
  const canShare = sharedQuery.data?.can_share ?? false;
  const sharedCounts = useSharedSegmentCounts(sharedQuery.data?.segments);
  const updateShared = useUpdateSharedSegment();
  const deleteShared = useDeleteSharedSegment();
  const [renaming, setRenaming] = useState<Renaming | null>(null);
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);

  function commitRename() {
    if (!renaming?.name.trim()) return;
    const name = renaming.name.trim();
    if (renaming.kind === 'shared') {
      updateShared.mutate({ id: renaming.id, body: { name } });
    } else if (renaming.kind === 'copy') {
      if (!stored) return;
      const id = stored.next_tab_id;
      saveTabs.mutate({
        ...stored,
        tabs: [...stored.tabs, { id, name, has_changes: false, filter_state: renaming.of.filter_state ?? null }],
        next_tab_id: id + 1,
      });
    } else if (stored) {
      const target = renaming.id;
      saveTabs.mutate({
        ...stored,
        tabs: stored.tabs.map((t) => (t.id === target ? { ...t, name } : t)),
      });
    }
    setRenaming(null);
  }

  function remove(t: FilterTab) {
    if (!stored || stored.tabs.length <= 1) return;
    if (!window.confirm(`Delete the "${t.name}" segment?`)) return;
    const remaining = stored.tabs.filter((x) => x.id !== t.id);
    saveTabs.mutate({
      ...stored,
      tabs: remaining,
      active_tab_id: stored.active_tab_id === t.id ? (remaining[0]?.id ?? null) : stored.active_tab_id,
    });
  }

  function removeShared(s: SharedSegment) {
    if (!window.confirm(`Delete the "${s.name}" segment for everyone it is shared with?`)) return;
    deleteShared.mutate(s.id);
  }

  const only = (stored?.tabs.length ?? 0) <= 1;
  const showShared = shared.length > 0 || canShare;

  return (
    <Stack gap="md">
      <PageHeader
        title={navLabel(v, 'segments')}
        actions={<SpeedChip stats={counts.data?.query_stats} />}
      />
      <Text size="sm" c="dimmed" maw={640}>
        {`Saved filters for ${v.many('lead')}. Create one from the ${navLabel(v, 'leads')} page with Save as segment; each count is live and matches what that segment shows there. A shared segment shares its filters, not its results: everyone sees only the ${v.many('lead')} their own login can see.`}
      </Text>

      {tabsQuery.error && <Alert color="red" variant="light">{tabsQuery.error.message}</Alert>}
      {counts.error && (
        <Alert color="orange" variant="light">
          {`Counts are unavailable right now: ${counts.error.message}`}
        </Alert>
      )}
      {deleteShared.error && <Alert color="red" variant="light">{deleteShared.error.message}</Alert>}

      <Section title="Mine">
        {tabsQuery.isPending ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : (
          <Table.ScrollContainer minWidth={520}>
            <Table verticalSpacing={8} highlightOnHover data-testid="segments-mine-table">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Segment</Table.Th>
                  <Table.Th ta="right">Count</Table.Th>
                  <Table.Th>Filters</Table.Th>
                  <Table.Th w={170} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(stored?.tabs ?? []).map((t) => (
                  <Table.Tr key={t.id}>
                    <Table.Td>
                      <Group gap={8} wrap="nowrap">
                        <Text
                          component={Link}
                          to={`/leads?segment=${t.id}`}
                          size="sm"
                          fw={600}
                          c="inherit"
                          style={{ textDecoration: 'none' }}
                        >
                          {t.name}
                        </Text>
                        {t.id === stored?.active_tab_id && (
                          <Text size="xs" c="dimmed">Current</Text>
                        )}
                        <UsedByButton
                          refType="segment"
                          refId={String(t.id)}
                          verb="Enrolled by"
                          hideWhenNone
                          title={`${v.Many('workflow')} that enrol it`}
                          testId={`segment-enrolled-by-${t.id}`}
                        />
                      </Group>
                    </Table.Td>
                    <Table.Td ta="right">
                      <CountCell n={counts.data?.totals.get(t.id)} pending={counts.isPending} />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{filtersText(activeFilterCount(segmentFilters(t)))}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        {canShare && (
                          <Tooltip label="Share" withArrow>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label={`Share ${t.name}`}
                              onClick={() => setShareTarget({ kind: 'private', tab: t })}
                            >
                              <IconShare size={15} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Tooltip label="Rename" withArrow>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={`Rename ${t.name}`}
                            onClick={() => setRenaming({ kind: 'private', id: t.id, name: t.name })}
                          >
                            <IconPencil size={15} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={only ? 'The last segment cannot be deleted' : 'Delete'} withArrow>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={`Delete ${t.name}`}
                            disabled={only}
                            onClick={() => remove(t)}
                          >
                            <IconTrash size={15} />
                          </ActionIcon>
                        </Tooltip>
                        <Button
                          size="compact-xs"
                          variant="light"
                          rightSection={<IconArrowRight size={13} />}
                          onClick={() => navigate(`/leads?segment=${t.id}`)}
                        >
                          Open
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Section>

      {showShared && (
        <Section title="Shared">
          {sharedQuery.isPending ? (
            <Group justify="center" p="xl"><Loader /></Group>
          ) : shared.length === 0 ? (
            <Text size="sm" c="dimmed" p="md">
              Nothing is shared yet. Share one of your segments to let others in the organisation open it.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={620}>
              <Table verticalSpacing={8} highlightOnHover data-testid="segments-shared-table">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Segment</Table.Th>
                    <Table.Th>Shared with</Table.Th>
                    <Table.Th ta="right">Count</Table.Th>
                    <Table.Th>Filters</Table.Th>
                    <Table.Th w={200} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {shared.map((s) => (
                    <Table.Tr key={s.id}>
                      <Table.Td>
                        <Group gap={8} wrap="nowrap" align="flex-start">
                          <IconUsers size={15} color={TOKENS.textMuted} style={{ marginTop: 2, flexShrink: 0 }} aria-hidden />
                          <Stack gap={0}>
                            <Text
                              component={Link}
                              to={`/leads?segment=${encodeURIComponent(sharedKey(s.id))}`}
                              size="sm"
                              fw={600}
                              c="inherit"
                              style={{ textDecoration: 'none' }}
                            >
                              {s.name}
                            </Text>
                            <Text size="xs" c="dimmed">{sharedByLine(s)}</Text>
                            <UsedByButton
                              refType="shared_segment"
                              refId={s.id}
                              verb="Enrolled by"
                              hideWhenNone
                              title={`${v.Many('workflow')} that enrol it`}
                              testId={`segment-enrolled-by-${s.id}`}
                            />
                          </Stack>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light" color="gray">
                          {s.visibility === 'everyone'
                            ? 'Everyone'
                            : s.can_edit
                              ? (s.shared_with.length === 1 ? '1 person' : `${s.shared_with.length} people`)
                              : 'Chosen people'}
                        </Badge>
                      </Table.Td>
                      <Table.Td ta="right">
                        <CountCell n={sharedCounts.totals?.get(s.id)} pending={sharedCounts.isPending} />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">{filtersText(activeFilterCount(toFilters(s.filter_state)))}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                          <Tooltip label="Save a copy to your own segments" withArrow>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label={`Save a copy of ${s.name}`}
                              onClick={() => setRenaming({ kind: 'copy', of: s, name: `${s.name} (copy)` })}
                            >
                              <IconCopy size={15} />
                            </ActionIcon>
                          </Tooltip>
                          {s.can_edit && (
                            <>
                              <Tooltip label="Share" withArrow>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  aria-label={`Share ${s.name}`}
                                  onClick={() => setShareTarget({ kind: 'shared', segment: s })}
                                >
                                  <IconShare size={15} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Rename" withArrow>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  aria-label={`Rename ${s.name}`}
                                  onClick={() => setRenaming({ kind: 'shared', id: s.id, name: s.name })}
                                >
                                  <IconPencil size={15} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Delete for everyone" withArrow>
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  aria-label={`Delete ${s.name}`}
                                  onClick={() => removeShared(s)}
                                >
                                  <IconTrash size={15} />
                                </ActionIcon>
                              </Tooltip>
                            </>
                          )}
                          <Button
                            size="compact-xs"
                            variant="light"
                            rightSection={<IconArrowRight size={13} />}
                            onClick={() => navigate(`/leads?segment=${encodeURIComponent(sharedKey(s.id))}`)}
                          >
                            Open
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Section>
      )}

      <ShareSegmentModal target={shareTarget} onClose={() => setShareTarget(null)} />

      <Modal
        opened={renaming !== null}
        onClose={() => setRenaming(null)}
        title={renaming?.kind === 'copy' ? 'Save a copy' : 'Rename segment'}
        size="sm"
      >
        <Stack>
          <TextInput
            data-autofocus
            label="Segment name"
            value={renaming?.name ?? ''}
            onChange={(e) => {
              const name = e.currentTarget.value;
              setRenaming((r) => (r ? { ...r, name } : r));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
            }}
          />
          {renaming?.kind === 'copy' && (
            <Text size="xs" c="dimmed">
              {`A private copy in your own segments. "${renaming.of.name}" stays as it is for everyone it is shared with.`}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRenaming(null)}>Cancel</Button>
            <Button onClick={commitRename} disabled={!renaming?.name.trim()}>Save</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
